import React, { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Lock, Mail, Loader2, Eye, EyeOff } from 'lucide-react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

const LoginPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Mouse Tracking for Glow Effect
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const logoRef = useRef<HTMLDivElement>(null);

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!logoRef.current) return;
        const rect = logoRef.current.getBoundingClientRect();
        // Calculamos la posición relativa al centro del contenedor para que el gradiente se note
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        mouseX.set(x);
        mouseY.set(y);
    };

    const springConfig = { damping: 25, stiffness: 400 };
    const xSpring = useSpring(mouseX, springConfig);
    const ySpring = useSpring(mouseY, springConfig);

    // Creamos el string del gradiente dinámico sincronizado con el mouse
    const mouseGlow = useTransform(
        [xSpring, ySpring],
        ([x, y]) => `radial-gradient(circle at ${x}px ${y}px, rgba(255, 255, 255, 0.15) 0%, transparent 80%)`
    );

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const { error: authError } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (authError) throw authError;

            // Al recargar, el App se encargará de mostrar el dashboard
            window.location.reload();
        } catch (err: any) {
            setError(err.message || 'Error al iniciar sesión');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-[100dvh] bg-black flex flex-col items-center justify-center p-4 selection:bg-white selection:text-black overflow-y-auto relative support-pwa">
            {/* Ambient background glow dots */}
            {/* Ambient background glow - Optimized for Safari */}
            <div className="fixed inset-0 pointer-events-none opacity-20">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[radial-gradient(circle,rgba(255,255,255,0.05)_0%,transparent_70%)]" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[radial-gradient(circle,rgba(255,255,255,0.03)_0%,transparent_70%)]" />
            </div>

            <div className="w-full max-w-md relative z-10">
                {/* Logo / Branding with Mouse Track Glow */}
                <div className="flex flex-col items-center mb-10 text-center">
                    <div className="mb-8">
                        <img
                            src="/CCSV_LOGO.svg"
                            alt="Carbon Case SV Logo"
                            className="h-28 w-auto"
                        />
                    </div>

                    <motion.h1
                        initial={{ letterSpacing: '0.1em', opacity: 0 }}
                        animate={{ letterSpacing: '0.4em', opacity: 1 }}
                        transition={{ duration: 1 }}
                        className="text-zinc-500 font-mono text-[10px] uppercase font-bold"
                    >
                        Portal Administrativo
                    </motion.h1>
                </div>

                {/* Login Card */}
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="bg-zinc-900/80 border border-zinc-800 p-8 relative overflow-hidden backdrop-blur-md rounded-sm"
                >
                    <div className="absolute top-0 left-0 w-1 h-full bg-white/60 shadow-[0_0_15px_rgba(255,255,255,0.3)]" />

                    <form onSubmit={handleLogin} className="space-y-6">
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="bg-red-500/10 border border-red-500/50 text-red-500 text-[10px] p-4 rounded-none text-center font-mono uppercase tracking-widest"
                            >
                                {error}
                            </motion.div>
                        )}

                        <div className="space-y-2">
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 ml-1">CORREO ELECTRÓNICO</label>
                            <div className="relative group/input">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-700 group-focus-within/input:text-white transition-colors" />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-black/60 border border-zinc-800 focus:border-white/50 focus:ring-1 focus:ring-white/10 outline-none text-white py-4 pl-12 rounded-none transition-all font-mono text-base md:text-sm"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 ml-1">CONTRASEÑA</label>
                            <div className="relative group/input">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-700 group-focus-within/input:text-white transition-colors" />
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-black/60 border border-zinc-800 focus:border-white/50 focus:ring-1 focus:ring-white/10 outline-none text-white py-4 pl-12 pr-12 rounded-none transition-all font-mono text-base md:text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-700 hover:text-white transition-colors p-1"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-white hover:bg-zinc-100 text-black font-display font-black uppercase py-4 px-6 transition-all tracking-[0.2em] text-xs shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_40px_rgba(255,255,255,0.5)] flex items-center justify-center gap-3 group relative overflow-hidden skew-x-[-10deg]"
                        >
                            {isLoading ? (
                                <Loader2 className="h-5 w-5 animate-spin skew-x-[10deg]" />
                            ) : (
                                <div className="flex items-center gap-3 skew-x-[10deg]">
                                    <span className="relative z-10">AUTENTICAR SISTEMA</span>
                                    <span className="relative z-10 group-hover:translate-x-1 transition-transform duration-300">→</span>
                                </div>
                            )}
                            {/* Shine effect */}
                            <div className="absolute inset-0 bg-white/40 translate-x-[-150%] skew-x-[20deg] group-hover:translate-x-[150%] transition-transform duration-700 ease-in-out" />
                        </button>
                    </form>
                </motion.div>

                <div className="mt-12 opacity-30 flex flex-col items-center gap-4">
                    <div className="flex items-center gap-6">
                        <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-zinc-700" />
                        <span className="text-zinc-600 text-[8px] uppercase tracking-[0.3em] font-bold whitespace-nowrap">
                            Acceso Restringido • Solo Personal Autorizado
                        </span>
                        <div className="h-[1px] w-12 bg-gradient-to-l from-transparent to-zinc-700" />
                    </div>
                </div>
            </div>
        </div >
    );
};

export default LoginPage;
